import { Button, Icon, Tag, Typography } from "antd";
import Checkbox, { CheckboxChangeEvent } from "antd/lib/checkbox";
import Table, { ColumnProps } from "antd/lib/table";
import { getEmployeeLocations } from "api/employeeLocationApi";
import { useUser } from "auth/UserHooks";
import ButtonRow from "components/common/ButtonRow";
import Modal from "components/common/Modal";
import SavePreferencesButton from "components/common/SavePreferencesButton";
import Stack from "components/common/Stack";
import React, { useEffect, useState } from "react";
import {
  EmployeeLocation,
  EmployeeLocationNotApplicable
} from "types/EmployeeLocation";
import sorters from "utils/sorters";
import useLocalStorage from "utils/useLocalStorage";
import EmployeeLocationForm from "./EmployeeLocationForm";
import { EmployeeRegion } from "../../../types/EmployeeRegion";
import displayErrorNotification from "../../../utils/displayErrorNotification";
import { getEmployeeRegions } from "../../../api/employeeRegionApi";

const EmployeeLocationLandingPage = () => {
  const user = useUser();
  const canManageEmployeeLocation = user.permissions.canManageEmployeeLocation;
  const [regions, setRegions] = useState<EmployeeRegion[]>([]);
  const [locations, setLocations] = useState<EmployeeLocation[]>([]);
  const [filteredLocations, setFilteredLocations] = useState<
    EmployeeLocation[]
  >([]);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showInactive, setShowInactive] = useLocalStorage<any>(
    "EmployeeLocationLandingPage_InactiveSetting",
    false
  );
  const [sortSettings, setSortSettings] = useLocalStorage<any>(
    "EmployeeLocationLandingPage_SortSettings",
    {
      key: "displayOrder ",
      order: "ascend"
    }
  );
  const [loading, setLoading] = useState(false);

  const _refreshLocation = () => {
    setLoading(true);
    return _loadLocation()
      .catch(displayErrorNotification)
      .then(() => {
        setLoading(false);
      });
  };

  const _loadLocation = () => {
    return getEmployeeLocations().then(response => {
      setLocations(response);
      filterLocations(response, showInactive);
    });
  };

  const _loadRegion = () => {
    return getEmployeeRegions().then(response => {
      setRegions(response);
    });
  };

  const filterLocations = (
    locations: EmployeeLocation[],
    showInactive: boolean
  ) => {
    setFilteredLocations(
      locations.filter((f: EmployeeLocation) => showInactive || !f.inactive)
    );
  };

  const handleInactiveChange = (e: CheckboxChangeEvent) => {
    setShowInactive(e.target.checked);
    filterLocations(locations, e.target.checked);
  };

  const regionSortValue = (record: EmployeeLocation) => {
    const find = regions.find(r => r.id === record.region);
    if (find !== undefined && find != null) {
      return find.name;
    } else {
      return "";
    }
  };

  let columns: ColumnProps<EmployeeLocation>[] = [
    {
      title: "Name",
      key: "name",
      dataIndex: "name",
      sorter: (a, b) => sorters.string(a.name, b.name)
    },
    {
      title: "Region",
      key: "region",
      sorter: (a, b) => sorters.string(regionSortValue(a), regionSortValue(b)),
      render: (item: EmployeeLocation) => {
        const find = regions.find(r => r.id === item.region);
        if (find !== undefined && find !== null) {
          return find.name;
        } else {
          return (
            <>
              <Tag color="red">Not found - Contact na_opsenablement@credera.com</Tag>
            </>
          );
        }
      }
    },
    {
      title: "Sort",
      key: "displayOrder",
      dataIndex: "displayOrder",
      sorter: (a, b) => a.displayOrder - b.displayOrder
    },
    {
      title: "Inactive",
      key: "inactive",
      render: (item: EmployeeLocation) =>
        item.inactive && <Icon type="check-circle" />
    }
  ];

  if (canManageEmployeeLocation) {
    columns.push({
      render: (item: EmployeeLocation) => (
        <>
          {item.id !== EmployeeLocationNotApplicable && (
            <Modal
              item={item}
              trigger={({ openModal }: { openModal: () => void }) => (
                <Button onClick={openModal} size="small" icon="edit">
                  Edit
                </Button>
              )}
            >
              {({
                item,
                closeModal
              }: {
                item: EmployeeLocation;
                closeModal: () => void;
              }) => (
                <EmployeeLocationForm
                  location={item}
                  regions={regions}
                  onSubmit={() => {
                    _refreshLocation().then(closeModal);
                  }}
                  onCancel={closeModal}
                  loading={loading}
                  setLoading={setLoading}
                />
              )}
            </Modal>
          )}
        </>
      )
    });
  }

  columns = columns.map(column => {
    if (column.key === sortSettings.key) {
      return { ...column, defaultSortOrder: sortSettings.order };
    }
    return column;
  });

  function onChange(_pagination: any, _filters: any, sorter: any) {
    if (Object.keys(sorter).length > 0) {
      setSortSettings({
        key: sorter.columnKey,
        order: sorter.order
      });
    }
  }

  useEffect(() => {
    setLoading(true);
    Promise.all([_loadLocation(), _loadRegion()]).then(() => {
      setLoading(false);
    });
  }, []);

  const _handleModalState = () => {
    setShowModal(!showModal);
  };

  return (
    <>
      {canManageEmployeeLocation && (
        <Modal isOpen={showModal} onClose={_handleModalState}>
          {({ closeModal }: { closeModal: () => void }) => (
            <EmployeeLocationForm
              regions={regions}
              onSubmit={() => {
                _refreshLocation().then(closeModal);
              }}
              onCancel={closeModal}
              loading={loading}
              setLoading={setLoading}
            />
          )}
        </Modal>
      )}
      <Stack>
        <Typography.Title level={1}>
          Employee Location
          <ButtonRow style={{ float: "right" }}>
            {canManageEmployeeLocation && (
              <Button icon="plus" onClick={_handleModalState}>
                Create Employee Location
              </Button>
            )}
            <SavePreferencesButton
              keys={[
                "EmployeeLocationLandingPage_InactiveSetting",
                "EmployeeLocationLandingPage_SortSettings"
              ]}
              disabled={loading}
            />
          </ButtonRow>
        </Typography.Title>
        <Checkbox
          checked={showInactive}
          onChange={handleInactiveChange}
          style={{ marginBottom: "20px" }}
        >
          Show Inactive
        </Checkbox>
        <Table
          columns={columns}
          dataSource={filteredLocations}
          loading={loading}
          rowKey="id"
          pagination={false}
          onChange={onChange}
        />
      </Stack>
    </>
  );
};

export default EmployeeLocationLandingPage;
